import { useQueries, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useEffect, useRef } from 'react';

import useHandleError from '@/hooks/useHandleError';
import {
  calculateStatusFromStatusCode,
  updateCloudSourceStatus
} from '@/pages/Administration/Devices/Devices.service';
import { cloudSourcesAtom } from '@/pages/Administration/Devices/Devices.store';
import {
  type Organization,
  selectedOrganizationAtom
} from '@/pages/Organizations';

import type { LimaSourcesResponseModal } from '../DevicesTable';
import type {
  LimaSourceActivationStatusDTO,
  LimaSourceStatusListDTO
} from '../DevicesTable/DevicesTable.types';
import {
  buildMicrosoftGroupedSources,
  shouldEnableActivateDeactivateMenu
} from '../SourceCreate/DataProvider/DataProvider.service';
import {
  filterAndEnrichCloudSources,
  getMissingSentinelOneManagedParentSourceUids,
  type SentinelOneParentSourceStatusMap
} from './CloudSourcesTable.utils';

type ParentSourceDetailResponse = {
  source_statuses?: LimaSourceStatusListDTO[];
};

type ParentSourceStatusResponse = {
  status?: string;
  status_label?: string;
};

type ParentSourceQueryResult = {
  data?: ParentSourceDetailResponse;
  isFetched: boolean;
  isError: boolean;
};

export const useFetchSourcesByOrg = (org: Organization) => {
  const url = `organizations/${org.label}/lima_sources`;

  const sourcesQuery = useQuery<LimaSourcesResponseModal[]>({
    queryKey: [url],
    staleTime: 30000
  });
  return sourcesQuery;
};

export const useFetchSourcesActivationByOrg = (org: Organization) => {
  const url = `organizations/${org.label}/lima_sources/activation_eligibility`;

  const sourcesActivationQuery = useQuery<LimaSourceActivationStatusDTO[]>({
    queryKey: [url],
    staleTime: 30000
  });
  return sourcesActivationQuery;
};

export const useCloudSourceListing = () => {
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);
  const [, setCloudSourcesData] = useAtom(cloudSourcesAtom);
  const lastSyncedCloudSourcesKeyRef = useRef<string>('');

  const sourcesQuery = useFetchSourcesByOrg(selectedOrgData);
  const sourcesActivationQuery =
    useFetchSourcesActivationByOrg(selectedOrgData);

  const handleError = useHandleError();

  const missingParentSourceUids = getMissingSentinelOneManagedParentSourceUids(
    sourcesQuery.data
  );

  const parentSourceQueries = useQueries({
    queries: missingParentSourceUids.map((parentSourceUid: string) => ({
      queryKey: [`lima_sources/${parentSourceUid}`],
      staleTime: 30000
    }))
  }) as ParentSourceQueryResult[];

  const parentSourceStatusMap: SentinelOneParentSourceStatusMap = (() => {
    const statusMap = new Map<string, ParentSourceStatusResponse>();

    parentSourceQueries.forEach((query, index) => {
      const parentSourceUid = missingParentSourceUids[index];
      const parentStatus = query.data?.source_statuses?.[0];

      if (parentSourceUid && parentStatus?.status_code) {
        statusMap.set(parentSourceUid, {
          status: calculateStatusFromStatusCode(
            parentStatus.status_code.toString()
          ),
          status_label: parentStatus.status_code_detail?.status_label || ''
        });
      }
    });

    return statusMap;
  })();

  const parentSourcesSettled =
    missingParentSourceUids.length === 0 ||
    parentSourceQueries.every((query) => query.isFetched || query.isError);

  // Step 1: Create activation map for quick lookup
  const activationMap = new Map(
    (sourcesActivationQuery.data || []).map(
      (status: LimaSourceActivationStatusDTO) => [status.source_uid, status]
    )
  );

  // Step 2: Merge activation eligibility into sources
  const sourcesWithActivation = !sourcesQuery.data
    ? []
    : sourcesQuery.data.map((source: LimaSourcesResponseModal) => {
        const activationStatus = activationMap.get(source.source_uid);

        return {
          ...source,
          allowSourceActivation: activationStatus
            ? shouldEnableActivateDeactivateMenu(activationStatus, source)
            : true
        };
      });

  // Step 3: Update cloud source status
  const sourcesWithStatus = sourcesWithActivation.map(updateCloudSourceStatus);

  // Step 4: Enrich SentinelOne child sources with parent status when needed
  const sourcesWithInheritedStatus = filterAndEnrichCloudSources(
    sourcesWithStatus,
    parentSourceStatusMap
  );

  // Step 5: Build Microsoft grouped sources
  const groupedSources = buildMicrosoftGroupedSources(
    sourcesWithInheritedStatus
  );

  // Sync transformed data to atom
  useEffect(() => {
    if (
      sourcesQuery.isSuccess &&
      sourcesActivationQuery.isSuccess &&
      parentSourcesSettled
    ) {
      const nextCloudSourcesKey = JSON.stringify(groupedSources);

      if (lastSyncedCloudSourcesKeyRef.current === nextCloudSourcesKey) {
        return;
      }

      lastSyncedCloudSourcesKeyRef.current = nextCloudSourcesKey;
      setCloudSourcesData(groupedSources);
    }
  }, [
    groupedSources,
    sourcesQuery.isSuccess,
    sourcesActivationQuery.isSuccess,
    parentSourcesSettled,
    setCloudSourcesData
  ]);

  // Handle errors separately
  useEffect(() => {
    if (sourcesQuery.isError || sourcesActivationQuery.isError) {
      setCloudSourcesData([]);
      handleError(
        (sourcesQuery.error || sourcesActivationQuery.error) as AxiosError,
        'Failure to fetch cloud sources.'
      );
    }
  }, [
    sourcesQuery.isError,
    sourcesActivationQuery.isError,
    sourcesQuery.error,
    sourcesActivationQuery.error,
    handleError,
    setCloudSourcesData
  ]);

  return {
    sourcesQuery,
    sourcesActivationQuery,
    isLoading:
      sourcesQuery.isLoading ||
      sourcesActivationQuery.isLoading ||
      (!parentSourcesSettled &&
        (sourcesQuery.isSuccess || sourcesActivationQuery.isSuccess))
  };
};
